import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  VpnKey as VpnKeyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';

const UserRolePage = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [buData, setBuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // API Functions
  const fetchBusinessUnits = async () => {
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/bu', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setBuData(result);
      console.log('Business units loaded:', result);
    } catch (error) {
      console.error('Error fetching business units:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://api-sso-v2.eurekagroup.id/api/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setUsers(result.data || result);
      console.log('Users loaded:', result);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch('https://api-sso-v2.eurekagroup.id/api/clients', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setClients(result.data || result);
      console.log('Clients loaded:', result);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUserRoles = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api-sso-v2.eurekagroup.id/api/user-roles', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const mappedData = (result.data || result).map(userRole => ({
        ...userRole,
        id: userRole._id
      }));
      setData(mappedData);
      console.log('User roles loaded:', result);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data user role: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteUserRole = async (id) => {
    try {
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/user-roles/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Remove from local state
      setData(prevData => prevData.filter(userRole => userRole.id !== id && userRole._id !== id));
      
      SwalConfig.success(
        'Berhasil Dihapus!',
        'Data user role telah berhasil dihapus dari sistem.',
        2000
      );
    } catch (error) {
      console.error('Error deleting user role:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal menghapus user role: ${error.message}`
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchBusinessUnits();
    fetchUsers();
    fetchClients();
    fetchUserRoles();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id) => {
    SwalConfig.deleteConfirm('user role ini').then((result) => {
      if (result.isConfirmed) {
        deleteUserRole(id);
      }
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'superadmin':
        return 'error';
      case 'user':
        return 'success';
      default:
        return 'default';
    }
  };

  const getBuName = (idBu) => {
    const bu = buData.find(item => item.bu_code === idBu.toString());
    return bu ? bu.bu_name : `BU ${idBu}`;
  };

  const getUserName = (userData) => {
    // Handle both object and string cases
    if (typeof userData === 'object' && userData !== null) {
      return userData.nama_lengkap || 'Unknown User';
    }
    const user = users.find(item => item._id === userData);
    return user ? user.nama_lengkap : 'Unknown User';
  };

  const getUserEmail = (userData) => {
    // Handle both object and string cases
    if (typeof userData === 'object' && userData !== null) {
      return userData.email || 'Unknown Email';
    }
    const user = users.find(item => item._id === userData);
    return user ? user.email : 'Unknown Email';
  };

  const getClientName = (clientData) => {
    // Handle both object and string cases
    if (typeof clientData === 'object' && clientData !== null) {
      return clientData.client || 'Unknown Client';
    }
    const client = clients.find(item => item._id === clientData);
    return client ? client.client : 'Unknown Client';
  };

  const filteredData = (data || [])
    .filter(userRole => {
      if (!userRole) return false;
      const userName = (getUserName(userRole.id_user) || '').toLowerCase();
      const clientName = (getClientName(userRole.id_client) || '').toLowerCase();
      const role = (userRole.role || '').toLowerCase();
      const search = (searchText || '').toLowerCase();

      const matchesSearch = userName.includes(search) ||
                           clientName.includes(search) ||
                           role.includes(search);
      const matchesRole = roleFilter === '' || userRole.role === roleFilter;
      
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => (getUserName(a?.id_user) || '').localeCompare(getUserName(b?.id_user) || ''));

  const uniqueRoles = [...new Set((data || []).map(userRole => userRole?.role).filter(Boolean))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ 
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 8px 32px rgba(0,0,0,0.5)' 
          : '0 8px 32px rgba(0,0,0,0.1)', 
        borderRadius: 3,
        background: theme.palette.background.paper
      }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <VpnKeyIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen User Role
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola role user untuk setiap client SSO
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={fetchUserRoles}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => navigate('/sso/user-role/add')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah User Role
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
         <Box sx={{ 
           mb: 3, 
           p: 3, 
           bgcolor: theme.palette.mode === 'dark' 
             ? theme.palette.action.hover 
             : 'grey.50', 
           borderRadius: 2,
           border: '1px solid',
           borderColor: theme.palette.mode === 'dark' 
             ? theme.palette.divider 
             : 'grey.200'
         }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari User Role"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={roleFilter}
                  onChange={handleRoleFilterChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'white'
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Role</em>
                  </MenuItem>
                  {uniqueRoles.map(role => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total User Role
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
         <Box sx={{ 
           border: '1px solid',
           borderColor: theme.palette.mode === 'dark' 
             ? theme.palette.divider 
             : 'grey.200',
           borderRadius: 2,
           overflow: 'hidden',
           background: theme.palette.background.paper
         }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data user role...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    No
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    minWidth: 200
                  }}>
                    User
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Business Unit
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}>
                    Aksi
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((userRole, index) => (
                   <TableRow 
                     key={userRole.id}
                     sx={{ 
                       '&:hover': { 
                         bgcolor: theme.palette.mode === 'dark' 
                           ? theme.palette.action.selected 
                           : 'grey.50',
                         transform: 'scale(1.001)',
                         transition: 'all 0.2s ease'
                       },
                       '&:nth-of-type(even)': {
                         bgcolor: theme.palette.mode === 'dark' 
                           ? theme.palette.action.hover 
                           : 'grey.25'
                       }
                     }}
                   >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {index + 1 + page * rowsPerPage}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: 'primary.main'
                          }}
                        >
                          <VpnKeyIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {getUserName(userRole.id_user)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {getUserEmail(userRole.id_user)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getClientName(userRole.id_client)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getBuName(userRole.id_bu)}
                        variant="outlined"
                        color="info"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userRole.role}
                        color={getRoleColor(userRole.role)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton 
                          onClick={() => navigate(`/sso/user-role/view/${userRole.id}`)} 
                          sx={{ 
                            color: 'success.main',
                            '&:hover': { 
                              bgcolor: 'success.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => navigate(`/sso/user-role/edit/${userRole.id}`)} 
                          sx={{ 
                            color: 'info.main',
                            '&:hover': { 
                              bgcolor: 'info.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(userRole.id)} 
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { 
                              bgcolor: 'error.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                 sx={{
                   borderTop: '1px solid',
                   borderColor: theme.palette.mode === 'dark' 
                     ? theme.palette.divider 
                     : 'grey.200',
                   bgcolor: theme.palette.mode === 'dark' 
                     ? theme.palette.action.hover 
                     : 'grey.50'
                 }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>
  );
};

export default UserRolePage;
